import { isSupported, setup } from '@loomhq/record-sdk';
import {
  type FC,
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { invariant } from '@/helpers/invariant';
import useNoodleApi from '@/hooks/useNoodleApi';
import { useUser } from '@/providers/Auth';
import { logError } from '@/providers/ErrorTracking';
import { type Job, JobType, useJobContext } from '@/providers/Jobs';
import { postLoomMedia } from '@/tsClient';
import getLoomToken from '@/tsClient/getLoomToken';
import { type Media } from '@/typings/graphql-models';

const LOOM_BUTTON_ID = 'loom-sdk-button';

type JobRef = {
  job?: Job;
  media?: Media;
};

type LoomContextValue = {
  isLoomSupported: boolean;
  recordVideo: (options: { jobCorrelationId?: string; onAttachLoomMedia?: (media: Media) => void }) => Promise<void>;
};

const LoomContext = createContext<LoomContextValue | undefined>(undefined);

export const useLoom = (): LoomContextValue => {
  const context = useContext(LoomContext);
  invariant(context, 'useLoom must be used within a LoomProvider');
  return context;
};

export const LoomProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isLoomSupported, setIsLoomSupported] = useState(true);
  const { addJob, updateJob } = useJobContext();
  const jobRef = useRef<JobRef>({});
  const [user] = useUser();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isInitialized = useRef(false);
  const setupPromiseRef = useRef<Promise<boolean> | null>(null);

  const optionsRef = useRef<{
    jobCorrelationId?: string;
    onAttachLoomMedia?: (media: Media) => void;
  }>({});

  const { getData: postLoomMediaFn } = useNoodleApi(postLoomMedia);
  const { getData: getLoomTokenFn } = useNoodleApi(getLoomToken);

  const setupLoom = useCallback(async (): Promise<boolean> => {
    if (isInitialized.current) {
      return true;
    }

    // Return existing promise if initialization is already in-flight
    if (setupPromiseRef.current) {
      return setupPromiseRef.current;
    }

    const setupPromise = (async (): Promise<boolean> => {
      try {
        let isSupportedCheck = false;
        let checkError = null;
        try {
          const { supported, error } = await isSupported();
          isSupportedCheck = supported;
          checkError = error;
          setIsLoomSupported(supported);
        } catch (e) {
          // Don't treat check errors as unsupported
          checkError = e;
          logError(e);
        }

        if (!isSupportedCheck) {
          if (checkError) {
            console.warn(`Error checking Loom support: ${checkError}`);
          }
          return false;
        }

        if (!user || user.isAnonymous || !user.isCreator) {
          return false;
        }

        const { data: token } = await getLoomTokenFn();
        if (!token) {
          // Token fetch failure is transient, don't mark as unsupported
          console.warn('Failed to fetch Loom token');
          return false;
        }

        try {
          const { configureButton } = await setup({
            jws: token,
          });

          const sdkButton = configureButton({ element: buttonRef.current || undefined });

          sdkButton.on('insert-click', async (video: { embedUrl: string }) => {
            const uploadFn = async (): Promise<Media | null> => {
              const { data: media } = await postLoomMediaFn({
                loomEmbedUrl: video.embedUrl,
                name: 'loom video',
                type: 'loomVideo',
              });
              if (media && jobRef.current.job) {
                updateJob({
                  id: jobRef.current.job.id,
                  uploadMedia: media,
                });
                optionsRef.current.onAttachLoomMedia?.(media);
              }
              return media;
            };
            jobRef.current.job = addJob({
              correlationId: optionsRef.current.jobCorrelationId,
              function: uploadFn,
              metadata: {
                isLoom: true,
              },
              title: 'Upload loom video',
              type: JobType.MESSAGE_MEDIA_UPLOAD,
            });
          });

          isInitialized.current = true;
          return true;
        } catch (e) {
          // SDK setup failure is transient, don't mark as unsupported
          logError(e);
          console.warn('Failed to configure Loom SDK');
          return false;
        }
      } finally {
        setupPromiseRef.current = null;
      }
    })();

    setupPromiseRef.current = setupPromise;
    return setupPromise;
  }, [user, getLoomTokenFn, postLoomMediaFn, addJob, updateJob]);

  const recordVideo = useCallback(
    async (newOptions: { jobCorrelationId?: string; onAttachLoomMedia?: (media: Media) => void }) => {
      optionsRef.current = newOptions;

      const isSetupSuccessful = await setupLoom();

      if (isSetupSuccessful) {
        buttonRef.current?.click();
      }
    },
    [setupLoom],
  );

  const value = useMemo(
    () => ({
      isLoomSupported,
      recordVideo,
    }),
    [isLoomSupported, recordVideo],
  );

  return (
    <LoomContext.Provider value={value}>
      {children}
      <button
        tabIndex={-1}
        ref={buttonRef}
        id={LOOM_BUTTON_ID}
        style={{
          left: '100%',
          opacity: 0,
          pointerEvents: 'none',
          position: 'fixed',
          top: '100%',
          zIndex: -1,
        }}
      >
        Loom Video
      </button>
    </LoomContext.Provider>
  );
};
